import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { Skeleton } from "@repo/ui/components/skeleton";
import { cn } from "@repo/ui/lib/utils";
import { useQuery } from "convex/react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  FileText,
  MinusCircle,
  Pencil,
  Plus,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const DAYS_PER_WEEK = 7;
export const EVENT_START_DATE = "2026-06-20";
export const EVENT_END_DATE = "2026-08-23";

const SCHEDULE_WEEK_RANGES = [
  { label: "Week 0.0", startDate: "2026-06-20", endDate: "2026-06-28" },
  { label: "Week 0", startDate: "2026-06-29", endDate: "2026-07-05" },
  { label: "Week 1", startDate: "2026-07-06", endDate: "2026-07-12" },
  { label: "Week 2", startDate: "2026-07-13", endDate: "2026-07-19" },
  { label: "Week 3", startDate: "2026-07-20", endDate: "2026-07-26" },
  { label: "Week 4", startDate: "2026-07-27", endDate: "2026-08-02" },
  { label: "Week 5", startDate: "2026-08-03", endDate: "2026-08-09" },
  { label: "Week 6", startDate: "2026-08-10", endDate: "2026-08-16" },
  { label: "Week 7", startDate: "2026-08-17", endDate: "2026-08-23" },
] as const;

export const EVENT_DAY_COUNT = daysBetweenInclusive(EVENT_START_DATE, EVENT_END_DATE);
export const SCHEDULE_WEEK_COUNT = SCHEDULE_WEEK_RANGES.length;

type PublishingState = "published" | "changes_pending" | "failed" | "draft" | "not_created";
type Staffing = {
  assigned: number;
  total: number;
  hasSchedule: boolean;
  hasGameTitles: boolean;
};
type Publishing = {
  state: PublishingState;
  lastPublishedAt?: string;
  lastDraftSavedAt?: string;
  lastAttemptAt?: string;
  pendingChangesCount?: number;
  discordThreadUrl?: string;
  errorMessage?: string;
};
type ScheduleDaySummary = {
  date: string;
  eventCoverage: Staffing;
  mediaDay: Staffing;
  publishing: Publishing;
};
type Week = {
  index: number;
  label: string;
  startDate: string;
  endDate: string;
  days: string[];
};

type WeekSummary = {
  notCreated: number;
  published: number;
  needsAttention: number;
  drafts: number;
  changes: number;
};

const stateText = {
  published: {
    title: "Published to Discord",
    action: "Open",
  },
  changes_pending: {
    title: "Changes pending",
    action: "Review Changes",
  },
  failed: {
    title: "Publish failed",
    action: "Retry",
  },
  draft: {
    title: "Draft",
    action: "Continue Draft",
  },
  not_created: {
    title: "Not created",
    action: "Create",
  },
} as const;

function parseScheduleDate(date: string) {
  return new Date(`${date}T00:00:00`);
}

function addDays(date: string, days: number) {
  const next = parseScheduleDate(date);
  next.setDate(next.getDate() + days);
  return next.toLocaleDateString("sv-SE");
}

function daysBetweenInclusive(startDate: string, endDate: string) {
  return (
    Math.floor(
      (parseScheduleDate(endDate).getTime() - parseScheduleDate(startDate).getTime()) / 86_400_000,
    ) + 1
  );
}

function isSameScheduleDay(a: Date, b: Date) {
  return a.toLocaleDateString("sv-SE") === b.toLocaleDateString("sv-SE");
}

function isPastScheduleDay(date: Date, today = new Date()) {
  return date.toLocaleDateString("sv-SE") < today.toLocaleDateString("sv-SE");
}

export function formatScheduleDate(date: string) {
  return parseScheduleDate(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatDateRange(startDate: string, endDate: string, withYear = false) {
  const start = parseScheduleDate(startDate);
  const end = parseScheduleDate(endDate);
  const startLabel = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endLabel = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: withYear ? "numeric" : undefined,
  });
  return `${startLabel} – ${endLabel}`;
}

export function formatRelativeOrAbsoluteTime(date?: string) {
  if (!date) return "";
  const value = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - value.getTime();
  const absMs = Math.abs(diffMs);
  const today = isSameScheduleDay(value, now);
  const yesterday = isSameScheduleDay(value, addDate(now, -1));

  if (absMs < 60_000) return "just now";
  if (absMs < 3_600_000) return `${Math.max(1, Math.round(absMs / 60_000))}m ago`;
  if (today && diffMs >= 0) return `${Math.round(absMs / 3_600_000)}h ago`;

  const time = value.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  if (yesterday) return `yesterday at ${time}`;

  return value.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function addDate(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function generateWeeks() {
  return SCHEDULE_WEEK_RANGES.map((week, index) =>
    Object.assign({ index }, week, {
      days: Array.from(
        { length: daysBetweenInclusive(week.startDate, week.endDate) },
        (_, dayIndex) => addDays(week.startDate, dayIndex),
      ),
    }),
  );
}

export function getWeekSummaries(days: ScheduleDaySummary[]) {
  return days.reduce<WeekSummary>(
    (summary, day) => {
      if (day.publishing.state === "not_created") summary.notCreated += 1;
      if (day.publishing.state === "published") summary.published += 1;
      if (day.publishing.state === "failed") summary.needsAttention += 1;
      if (day.publishing.state === "draft") summary.drafts += 1;
      if (day.publishing.state === "changes_pending") {
        summary.changes += day.publishing.pendingChangesCount ?? 1;
      }
      return summary;
    },
    { notCreated: 0, published: 0, needsAttention: 0, drafts: 0, changes: 0 },
  );
}

export function getSelectedWeek(
  today: Date,
  eventStartDate: string,
  weeks: Week[],
  days: ScheduleDaySummary[],
) {
  const todayKey = today.toLocaleDateString("sv-SE");
  const todayWeek = weeks.find((week) => week.days.includes(todayKey));
  if (todayWeek) return todayWeek.index;

  if (todayKey < eventStartDate) return 0;

  const lastActiveDay = [...days]
    .toReversed()
    .find(
      (day) =>
        day.eventCoverage.hasGameTitles ||
        day.mediaDay.hasGameTitles ||
        day.publishing.state !== "not_created",
    );
  const lastActiveWeek = lastActiveDay
    ? weeks.find((week) => week.days.includes(lastActiveDay.date))
    : undefined;

  return lastActiveWeek?.index ?? weeks.at(-1)?.index ?? 0;
}

export function getPublishingLabel(publishing: Publishing) {
  const mainText = stateText[publishing.state].title;
  const supportingText = (() => {
    if (publishing.state === "published") {
      return `Synced ${formatRelativeOrAbsoluteTime(publishing.lastPublishedAt)}`;
    }
    if (publishing.state === "changes_pending") {
      return `${publishing.pendingChangesCount ?? 0} changes not published`;
    }
    if (publishing.state === "failed") {
      return publishing.lastAttemptAt
        ? `Last attempt ${formatRelativeOrAbsoluteTime(publishing.lastAttemptAt)}`
        : "Last attempt failed";
    }
    if (publishing.state === "draft") return "Not published yet";
    return "No schedule for this day";
  })();

  return { mainText, supportingText };
}

export function getPrimaryActionForPublishingState(publishing: Publishing) {
  return stateText[publishing.state].action;
}

export function ScheduleOverview({
  weekParam,
  onWeekChange,
  onOpenDay,
}: {
  weekParam?: number;
  onWeekChange: (week: number) => void;
  onOpenDay: (date: string, options?: { reviewChanges?: boolean }) => void;
}) {
  const weeks = React.useMemo(() => generateWeeks(), []);
  const overviewDays = useQuery(api.schedule.listOverviewDays, {
    startDate: EVENT_START_DATE,
    days: EVENT_DAY_COUNT,
  });

  if (!overviewDays) return <ScheduleOverviewSkeleton />;

  const days = overviewDays as ScheduleDaySummary[];
  const selectedWeekIndex = weekParam
    ? Math.min(Math.max(weekParam - 1, 0), SCHEDULE_WEEK_COUNT - 1)
    : getSelectedWeek(new Date(), EVENT_START_DATE, weeks, days);
  const selectedWeek = weeks[selectedWeekIndex] ?? weeks[0];
  const daysByDate = new Map(days.map((day) => [day.date, day]));
  const selectedDays = selectedWeek.days.map(
    (date) => daysByDate.get(date) ?? createEmptySummary(date),
  );
  const summary = getWeekSummaries(selectedDays);

  return (
    <div className="min-h-full bg-background p-4 text-foreground md:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
        <WeekTabs weeks={weeks} selectedWeekIndex={selectedWeekIndex} onWeekChange={onWeekChange} />
        <WeeklySummary summary={summary} totalDays={selectedDays.length} />
        <section aria-label="Call-times days" className="space-y-2">
          <div className="space-y-1.5">
            {selectedDays.map((day) => {
              const date = parseScheduleDate(day.date);
              const showTodaySeparator =
                isSameScheduleDay(date, new Date()) && isPastScheduleDay(addDate(date, -1));
              const hasGames = day.eventCoverage.hasGameTitles || day.mediaDay.hasGameTitles;

              return (
                <React.Fragment key={day.date}>
                  {showTodaySeparator ? <TodaySeparator /> : null}
                  <DayRow day={day} hasGames={hasGames} onOpenDay={onOpenDay} />
                </React.Fragment>
              );
            })}
          </div>
        </section>
        <p className="text-center text-xs text-muted-foreground/70">
          All times are in local event time.
        </p>
      </div>
    </div>
  );
}

function WeekTabs({
  weeks,
  selectedWeekIndex,
  onWeekChange,
}: {
  weeks: Week[];
  selectedWeekIndex: number;
  onWeekChange: (week: number) => void;
}) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border bg-background/80 sm:grid-cols-5 xl:grid-cols-10">
      {weeks.map((week) => {
        const todayKey = new Date().toLocaleDateString("sv-SE");
        const isActive = week.index === selectedWeekIndex;
        const isCurrentWeek = week.days.includes(todayKey);
        const isPastWeek = week.endDate < todayKey;
        return (
          <button
            key={week.label}
            type="button"
            aria-pressed={isActive}
            className={cn(
              "min-h-20 cursor-pointer overflow-hidden border-border px-4 py-3 text-center transition-colors first:rounded-l-lg last:rounded-r-lg focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
              "border-r border-b last:border-r-0 xl:border-b-0",
              isActive
                ? "bg-accent text-accent-foreground ring-1 ring-primary/50 ring-inset"
                : "text-muted-foreground hover:bg-accent/70 hover:text-accent-foreground",
              isPastWeek && !isActive && "opacity-45 hover:opacity-75",
              isCurrentWeek && !isActive && "ring-1 ring-primary/25 ring-inset",
            )}
            onClick={() => onWeekChange(week.index + 1)}
          >
            <span className="block text-sm font-semibold md:text-base">{week.label}</span>
            <span className="mt-1 block text-xs text-muted-foreground md:text-sm">
              {formatDateRange(week.startDate, week.endDate)}
            </span>
            {isCurrentWeek ? (
              <span className="mt-2 inline-flex rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold tracking-wide text-primary-foreground uppercase">
                Current week
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function WeeklySummary({ summary, totalDays }: { summary: WeekSummary; totalDays: number }) {
  const metrics = [
    {
      label: "Not created",
      value: summary.notCreated,
      icon: MinusCircle,
      className: "text-muted-foreground bg-muted",
    },
    {
      label: "Drafts",
      value: summary.drafts,
      icon: FileText,
      className: "text-chart-1 bg-chart-1/15",
    },
    {
      label: "Needs Attention",
      value: summary.needsAttention,
      icon: AlertTriangle,
      className: "text-chart-3 bg-chart-3/15",
    },
    {
      label: "Published",
      value: `${summary.published} / ${totalDays}`,
      icon: CheckCircle2,
      className: "text-chart-2 bg-chart-2/15",
    },
    {
      label: "Unsaved Changes",
      value: summary.changes,
      icon: RotateCcw,
      className: "text-chart-4 bg-chart-4/15",
    },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card/80 shadow-sm">
      <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-5 lg:items-center">
        {metrics.map((metric, index) => (
          <div
            key={metric.label}
            className={cn(
              "flex items-center gap-3 border-border",
              index > 0 && "lg:border-l lg:pl-6",
            )}
          >
            <span
              className={cn(
                "flex size-9 items-center justify-center rounded-full",
                metric.className,
              )}
            >
              <metric.icon className="size-5" aria-hidden="true" />
            </span>
            <div>
              <div className="text-lg font-semibold text-foreground">{metric.value}</div>
              <div className="text-sm text-muted-foreground">{metric.label}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="hidden grid-cols-[130px_minmax(360px,1fr)_minmax(280px,0.8fr)_180px] border-t border-border bg-muted/30 px-5 py-3 text-center text-xs font-semibold tracking-[0.22em] text-muted-foreground uppercase lg:grid">
        <span>Date</span>
        <span>Staffing</span>
        <span>Publishing</span>
        <span>Action</span>
      </div>
    </div>
  );
}

function DayRow({
  day,
  hasGames,
  onOpenDay,
}: {
  day: ScheduleDaySummary;
  hasGames: boolean;
  onOpenDay: (date: string, options?: { reviewChanges?: boolean }) => void;
}) {
  const date = parseScheduleDate(day.date);
  const isToday = isSameScheduleDay(date, new Date());
  const isPast = isPastScheduleDay(date);
  const publishingLabel = getPublishingLabel(day.publishing);
  const actionLabel = getPrimaryActionForPublishingState(day.publishing);
  const disabled = !hasGames;
  const ActionIcon = {
    published: ExternalLink,
    changes_pending: ArrowRight,
    failed: RotateCcw,
    draft: Pencil,
    not_created: Plus,
  }[day.publishing.state];

  const handleAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (day.publishing.state === "failed") {
      toast.info("Retry publishing is not available from the overview yet.", {
        description: "Opening the day schedule so you can publish again.",
      });
    }
    onOpenDay(day.date, { reviewChanges: day.publishing.state === "changes_pending" });
  };

  return (
    <div
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      className={cn(
        "grid gap-4 rounded-lg border bg-card/70 p-4 transition-colors lg:grid-cols-[130px_minmax(360px,1fr)_minmax(280px,0.8fr)_180px] lg:items-center",
        disabled
          ? "pointer-events-none border-muted/40 opacity-30"
          : [
              "cursor-pointer hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              isToday ? "border-primary/60 ring-1 ring-primary/20 ring-inset" : "border-border",
              isPast && "opacity-55 hover:opacity-80",
            ],
      )}
      onClick={disabled ? undefined : () => onOpenDay(day.date)}
      onKeyDown={
        disabled
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenDay(day.date);
              }
            }
      }
    >
      <DateCell date={date} isToday={isToday} />
      <div className="flex flex-col justify-center space-y-2 self-stretch border-border lg:border-l lg:pl-5">
        <StaffingLine label="Event Coverage" staffing={day.eventCoverage} tone="event" />
        <StaffingLine label="Media Day" staffing={day.mediaDay} tone="media" />
      </div>
      <PublishingState publishing={day.publishing} label={publishingLabel} />
      <div className="flex items-center justify-start self-stretch border-border lg:justify-end">
        {disabled ? (
          <span className="text-sm font-medium text-muted-foreground/60">No games</span>
        ) : (
          <Button
            type="button"
            size="lg"
            variant="secondary"
            className={cn(
              "w-full max-w-[190px] cursor-pointer gap-2 text-white shadow-sm",
              day.publishing.state === "published" && "bg-chart-2 hover:bg-chart-2/85",
              day.publishing.state === "changes_pending" && "bg-chart-4 hover:bg-chart-4/85",
              day.publishing.state === "failed" && "bg-destructive hover:bg-destructive/85",
              day.publishing.state === "draft" && "bg-chart-1 hover:bg-chart-1/85",
              day.publishing.state === "not_created" &&
                "bg-muted text-foreground hover:bg-muted/80",
            )}
            aria-label={`${actionLabel} schedule for ${day.date}`}
            onClick={handleAction}
          >
            <ActionIcon className="size-4" aria-hidden="true" />
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

function TodaySeparator() {
  return (
    <div className="flex items-center gap-3 py-1" aria-label="Current and upcoming days start here">
      <div className="h-px flex-1 bg-border" />
      <span className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
        Current and upcoming days
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function DateCell({ date, isToday }: { date: Date; isToday: boolean }) {
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });

  if (isToday) {
    return (
      <div className="flex w-20 flex-col items-center gap-1">
        <div className="w-full rounded-lg bg-primary px-3 py-2 text-center leading-none text-primary-foreground">
          <div className="text-xs font-bold tracking-wide uppercase">{month}</div>
          <div className="mt-1 text-3xl font-bold">{date.getDate()}</div>
          <div className="mt-1 text-xs font-bold tracking-wide uppercase">{weekday}</div>
        </div>
        <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          Today
        </span>
      </div>
    );
  }

  return (
    <div className="w-20 text-center leading-none">
      <div className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {month}
      </div>
      <div className="mt-1 text-3xl font-semibold text-foreground">{date.getDate()}</div>
      <div className="mt-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {weekday}
      </div>
    </div>
  );
}

function StaffingLine({
  label,
  staffing,
  tone,
}: {
  label: string;
  staffing: ScheduleDaySummary["eventCoverage"];
  tone: "event" | "media";
}) {
  const progress =
    staffing.total > 0 ? Math.min(100, (staffing.assigned / staffing.total) * 100) : 0;
  const isComplete = staffing.total > 0 && staffing.assigned >= staffing.total;
  const barColor = !staffing.hasGameTitles
    ? "bg-muted"
    : !staffing.hasSchedule
      ? "bg-muted-foreground/20"
      : isComplete
        ? "bg-chart-2"
        : tone === "media"
          ? "bg-chart-1"
          : "bg-chart-3";

  return (
    <div className="grid gap-2 text-sm sm:grid-cols-[130px_minmax(120px,1fr)_150px] sm:items-center">
      <div className="font-medium text-foreground">{label}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div className={cn("h-full rounded-full", barColor)} style={{ width: `${progress}%` }} />
      </div>
      <div className="text-muted-foreground">
        {staffing.hasGameTitles
          ? staffing.hasSchedule
            ? `${staffing.assigned} / ${staffing.total} assigned`
            : "Not staffed yet"
          : "No games today"}
      </div>
    </div>
  );
}

function PublishingState({
  publishing,
  label,
}: {
  publishing: Publishing;
  label: { mainText: string; supportingText: string };
}) {
  const Icon = {
    published: CheckCircle2,
    changes_pending: AlertTriangle,
    failed: AlertTriangle,
    draft: FileText,
    not_created: MinusCircle,
  }[publishing.state];
  const iconClassName = {
    published: "bg-chart-2/15 text-chart-2",
    changes_pending: "bg-chart-3/15 text-chart-3",
    failed: "bg-destructive/15 text-destructive",
    draft: "bg-chart-1/15 text-chart-1",
    not_created: "bg-muted text-muted-foreground",
  }[publishing.state];

  return (
    <div className="flex items-center gap-3 self-stretch border-border lg:border-l lg:pl-6">
      <span
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          iconClassName,
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="font-semibold text-foreground">{label.mainText}</div>
        <div className="truncate text-sm text-muted-foreground">{label.supportingText}</div>
      </div>
    </div>
  );
}

function createEmptySummary(date: string): ScheduleDaySummary {
  return {
    date,
    eventCoverage: { assigned: 0, total: 0, hasSchedule: false, hasGameTitles: false },
    mediaDay: { assigned: 0, total: 0, hasSchedule: false, hasGameTitles: false },
    publishing: {
      state: "not_created",
      lastPublishedAt: undefined,
      lastDraftSavedAt: undefined,
      lastAttemptAt: undefined,
      pendingChangesCount: undefined,
      discordThreadUrl: undefined,
      errorMessage: undefined,
    },
  };
}

function ScheduleOverviewSkeleton() {
  return (
    <div className="min-h-full bg-background p-4 text-foreground md:p-6">
      <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-muted sm:grid-cols-5 xl:grid-cols-10">
          {Array.from({ length: SCHEDULE_WEEK_COUNT }, (_, index) => (
            <div key={index} className="bg-background p-4">
              <Skeleton className="mx-auto h-5 w-20 bg-muted" />
              <Skeleton className="mx-auto mt-2 h-4 w-24 bg-muted" />
            </div>
          ))}
        </div>
        <Skeleton className="h-24 rounded-lg bg-muted" />
        <div className="space-y-2">
          {Array.from({ length: DAYS_PER_WEEK }, (_, index) => (
            <Skeleton key={index} className="h-24 rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    </div>
  );
}
